import { Routes } from "@angular/router";
import { StorybookComponent } from "./components/pages/storybook/storybook.component";
import { Provider } from "@angular/core";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { UpdateUrlInterceptor } from "./interceptors/update-url.interceptor";
import { PageLoaderInterceptor } from "./interceptors/page-loader.interceptor";
import { UnauthorizedInterceptor } from "./interceptors/unauthorized.interceptor";
import { ToastInterceptor } from "./interceptors/toast.interceptor";
import { authFlowGuard, appGuard, authGuard, roleGuard } from "./guards";

/** Routes configuration for the application */
export const routes: Routes = [
  {
    path: "google-auth-callback",
    loadComponent: () =>
      import(
        "./common/auth/src/components/pages/google-auth-callback/google-auth-callback.component"
      ).then((c) => c.GoogleAuthCallbackComponent),
  },
  {
    path: "",
    component: StorybookComponent,
    title: "Talea Clinic",
    children: [
      {
        path: "",
        canActivate: [authFlowGuard],
        loadComponent: () =>
          import("./components/pages/auth-flow/auth-flow.component").then(
            (c) => c.AuthFlowComponent
          ),
      },
      {
        path: "google-calendar-callback",
        loadComponent: () =>
          import(
            "./components/pages/google-calendar-callback/google-calendar-callback.component"
          ).then((c) => c.GoogleCalendarCallbackComponent),
      },
      {
        path: "home",
        loadComponent: () =>
          import("./components/pages/patients/home/home.component").then(
            (c) => c.HomeComponent,
          ),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "payment-success/home",
        loadComponent: () =>
          import("./components/pages/payment-redirect/payment-redirect.component").then(
            (c) => c.PaymentRedirectComponent,
          ),
      },
      {
        path: "my-consultations",
        loadComponent: () =>
          import(
            "./components/pages/patients/consultations/my-consultations/my-consultations.component"
          ).then((c) => c.MyConsultationsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-consultations/booking",
        loadComponent: () =>
          import(
            "./components/pages/patients/consultations/booking-consultation/booking-consultation.component"
          ).then((c) => c.BookingConsultationComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-prescriptions",
        loadComponent: () =>
          import(
            "./components/pages/patients/prescriptions/my-prescriptions/my-prescriptions.component"
          ).then((c) => c.MyPrescriptionsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-prescriptions/booking",
        loadComponent: () =>
          import(
            "./components/pages/patients/prescriptions/booking-prescription/booking-prescription.component"
          ).then((c) => c.BookingPrescriptionComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-doctor-team",
        loadComponent: () =>
          import(
            "./components/pages/patients/doctors/my-doctor-team/my-doctor-team.component"
          ).then((c) => c.MyDoctorTeamComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-doctor-team/:id",
        loadComponent: () =>
          import(
            "./components/pages/patients/doctors/my-doctor-team-details/my-doctor-team-details.component"
          ).then((c) => c.MyDoctorTeamDetailsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-patients",
        loadComponent: () =>
          import(
            "./components/pages/doctors/patients/my-patients/my-patients.component"
          ).then((c) => c.MyPatientsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'psycho'],
        },
      },
      {
        path: "my-patients/:id",
        loadComponent: () =>
          import(
            "./components/pages/doctors/patients/patient-details/patient-details.component"
          ).then((c) => c.PatientDetailsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'psycho'],
        },
      },
      {
        path: "my-patients/:id/:pathway",
        loadComponent: () =>
          import(
            "./components/pages/doctors/pathways/pathway-details/pathway-details.component"
          ).then((c) => c.PathwayDetailsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'psycho'],
        },
      },
      {
        path: "patients-documents",
        loadComponent: () =>
          import(
            "./components/pages/doctors/patients/patients-documents/patients-documents.component"
          ).then((c) => c.PatientsDocumentsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'clinic', 'psycho'],
        },
      },
      {
        path: "patients-documents/:id",
        loadComponent: () =>
          import(
            "./components/pages/doctors/patients/patients-documents-detail/patients-documents-detail.component"
          ).then((c) => c.PatientsDocumentsDetailComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'clinic', 'psycho'],
        },
      },
      {
        path: "my-documents",
        loadComponent: () =>
          import(
            "./components/pages/patients/documents/my-documents/my-documents.component"
          ).then((c) => c.MyDocumentsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "my-pathway",
        loadComponent: () =>
          import(
            "./components/pages/patients/my-pathway/my-pathway.component"
          ).then((c) => c.MyPathwayComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient'],
        },
      },
      {
        path: "agenda",
        loadComponent: () =>
          import("./components/pages/doctors/agenda/agenda.component").then(
            (c) => c.AgendaComponent,
          ),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'clinic', 'psycho'],
        },
      },
      {
        path: "my-appointments",
        loadComponent: () =>
          import(
            "./components/pages/doctors/appointments/my-appointments/my-appointments.component"
          ).then((c) => c.MyAppointmentsComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['doctor', 'psycho'],
        },
      },
      {
        path: "bookings",
        loadComponent: () =>
          import("./components/pages/clinics/bookings/bookings.component").then(
            (c) => c.BookingsComponent,
          ),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['clinic'],
        },
      },
      {
        path: "pathways-management",
        loadComponent: () =>
          import(
            "./components/pages/clinics/pathways-management/pathways-management.component"
          ).then((c) => c.PathwaysManagementComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['clinic'],
        },
      },
      {
        path: "pathways-management/:id",
        loadComponent: () =>
          import(
            "./components/pages/clinics/pathways-management-detail/pathways-management-detail.component"
          ).then((c) => c.PathwaysManagementDetailComponent),
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['clinic', 'doctor'],
        },
      },
      {
        path: "account",
        canActivate: [appGuard, roleGuard],
        data: {
          roles: ['patient', 'doctor', 'clinic', 'psycho'],
        },
        children: [
          {
            path: "",
            redirectTo: "profile",
            pathMatch: "full",
          },
          {
            path: "profile",
            loadComponent: () =>
              import("./components/pages/account/account.component").then(
                (c) => c.AccountComponent,
              ),
          },
          {
            path: "notifications",
            loadComponent: () =>
              import("./components/pages/account/account.component").then(
                (c) => c.AccountComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: "**",
    redirectTo: "",
  },
];

/** provider configuration for the AuthInterceptor to be used as an HTTP_INTERCEPTOR */
const authInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};

/** provider configuration for the UpdateUrlInterceptor to be used as an HTTP_INTERCEPTOR */
const updateUrlInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: UpdateUrlInterceptor,
  multi: true,
};

/** provider configuration for the PageLoaderInterceptor to be used as an HTTP_INTERCEPTOR */
const pageLoaderInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: PageLoaderInterceptor,
  multi: true,
};

/** provider configuration for the UnauthorizedInterceptor to be used as an HTTP_INTERCEPTOR */
const unauthorizedInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: UnauthorizedInterceptor,
  multi: true,
};

/** provider configuration for the ToastInterceptor to be used as an HTTP_INTERCEPTOR */
const toastInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ToastInterceptor,
  multi: true,
};

/** provider configuration for the Thalea interceptors */
export const thaleaProviders: Provider[] = [
  authInterceptorProvider,
  updateUrlInterceptorProvider,
  pageLoaderInterceptorProvider,
  unauthorizedInterceptorProvider,
  toastInterceptorProvider,
];
